import React, { useEffect, useState } from "react";
import EmployeeManager from "../../../managers/EmployeeManager";
import { useMsal } from "@azure/msal-react";
import { Employee, EmployeeListPageProps } from "../../../interfaces/EmployeeInterface";
import "./V1EmployeeListPage.css";
import Colors from '../../../styles/Colors.json';
import { useSiteContext } from "../../../SiteContext";
import { PunchType, TimeEntryType, TimesheetType } from "../../../interfaces/TimesheetInterface";
import { TimeEntryData } from "../../../interfaces/V1Interface";
import V1ExportManager from "../../../managers/V1ExportManager";
import PageWrapper from "../../../components/PageWrapper";
import { convertToLocalTime } from "../../../util/DateAndTimeUtil";
import SearchBar from "../../../components/SearchBar";

const V1EmployeeListPage: React.FC<EmployeeListPageProps> = ({ setError }) => {
  const { 
    setLoading,
    type, 
    payPeriodID,
    superID, 
    superName, 
    translator,
    superAltID,
    createPopup,
    userRole,
    userID
  } = useSiteContext();
  
  const [data, setData] = useState<Employee[] | null>(null);
  const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>(""); // State to manage the search term
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);

  const { instance, accounts } = useMsal();

  useEffect(() => {
    const fetchData = async () => {
      if (payPeriodID == null || superID == null) return;

      setLoading(true);

      try {
        const adminUserID = userRole.admin || userRole.payroll || userRole.hr ? null : userID;
        const response = await EmployeeManager.getEmployees(payPeriodID, TimesheetType.PayApproved, superID, adminUserID);
        setData(response.data);
        // Automatically select all employees when data is fetched
        const allEmployeeIDs = response.data.map((employee: { altEmployeeID: any; }) => employee.altEmployeeID || '');
        setSelectedEmployees(new Set(allEmployeeIDs)); // Set all checkboxes as checked initially
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to fetch data. Please try again later.");
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [payPeriodID, type, instance, accounts, setError, setLoading]);

  useEffect(() => {
    // Clear the selection whenever the search term changes
    setSelectedEmployees(new Set());
    setSelectAll(false);
  }, [searchTerm]);

  useEffect(() => {
    if(data){

      const filteredList = data.filter((employee) => {
        const searchTermLower = searchTerm.toLowerCase(); // Convert search term to lowercase for case-insensitive comparison
    
        // Check if the search term contains letters (name search)
        if (/[a-zA-Z]/.test(searchTerm)) {
          const fullName = `${employee.name.first} ${employee.name.last}`.toLowerCase();
          return fullName.includes(searchTermLower); // Filter by name
        }
    
        // Otherwise, filter by altEmployeeID (number search)
        return employee.altEmployeeID?.toString().includes(searchTerm);
      })

      setFilteredEmployees(filteredList);

      if(searchTerm == '' && compareLists(selectedEmployees, filteredEmployees)){setSelectAll(true)};

    }
    
  }, [setData, data, setSearchTerm, searchTerm]);

  const handleEmployeeCheckboxChange = (employeeID: string) => {
    setSelectedEmployees((prevSelectedEmployees) => {
      const newSelectedEmployees = new Set(prevSelectedEmployees);
      if (newSelectedEmployees.has(employeeID)) {
        newSelectedEmployees.delete(employeeID);
      } else {
        newSelectedEmployees.add(employeeID);
      }
  
      // Convert the Set to an array, sort it, and convert it back to a Set
      const sortedEmployeeIDs = Array.from(newSelectedEmployees)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })); // Sort numerically
  
      const sortedSet = new Set(sortedEmployeeIDs);
  
      setSelectAll(compareLists(sortedSet, filteredEmployees));
      return sortedSet;
    });
  };
  
  const compareLists = (listA: Set<string>, listB: Employee[]) => {
    const listBIDs = new Set(listB.map((employee) => employee.altEmployeeID || ''));
    return (
      listA.size === listBIDs.size &&
      Array.from(listA).every((id) => listBIDs.has(id))
    );
  };
  
  const handleSelectAllChange = () => {
    setSelectedEmployees((prevSelectedEmployees) => {
      if (selectAll) {
        return new Set(); // Unselect all
      } else {
        const allFilteredIDs = new Set(filteredEmployees.map((employee) => employee.altEmployeeID || ''));
        return new Set(allFilteredIDs); // Select all filtered employees
      }
    });
    setSelectAll(!selectAll);
  };

  const subtractOneYear = (dateStr: string) => {
    const date = new Date(dateStr);  // Convert the string to a Date object
    date.setFullYear(date.getFullYear() - 1);  // Subtract one year
    return date.toISOString();  // Convert back to ISO 8601 format (includes time)
  };

  // Function to prepare the time data
  const prepareTimesData = (employee: Employee): TimeEntryData[] => {
    return employee.days.flatMap((day) =>
      day.timeEntries.map((entry) => ({
        employeeId: employee.altEmployeeID.padStart(5, '0'),
        supervisorId: superAltID,
        dataType: "STARTSTOP",
        inTime: convertToLocalTime(entry.inDateTime, entry.inDateTimeOffset).toISOString(), // Convert to string
        inOffset: entry.inDateTimeOffset,
        outTime: convertToLocalTime(entry.outDateTime, entry.outDateTimeOffset).toISOString(), // Convert to string
        outOffset: entry.outDateTimeOffset,
        punchType: "Regular",
        earningType: entry.type === TimeEntryType.Regular ? "REG" : "MEAL",
        punchFromAppType: 0
      }))
    );
  };

  const V1Export = async () => {
    // Filter the selectedEmployees to include only those in the filteredEmployees list
    const filteredSelectedEmployees = new Set(
      Array.from(selectedEmployees).filter((employeeID) =>
        filteredEmployees.some((employee) => employee.altEmployeeID === employeeID)
      )
    );
  
    try {
      // Set loading state before sending requests
      setLoading(true);
  
      // Loop through each selected employee
      for (const employeeID of filteredSelectedEmployees) {
        // Find the employee based on the selected employee ID
        const employee = data?.find((e) => e.altEmployeeID === employeeID);
        if (employee) {
          // Prepare time data for this employee
          const employeeTimeData = prepareTimesData(employee);
  
          // Create the payload for this employee
          const timesPayload = { times: employeeTimeData };
  
          // Log the payload for verification
          console.log("Payload to send for employee:", employeeID, timesPayload);
  
          // Send the data for this employee
          const response = await V1ExportManager.exportV1Data(timesPayload);
          console.log("API response for employee:", employeeID, response);
        }
      }
  
      // Show success message after all employees are processed
      createPopup("Export...", "Successful!");
    } catch (error) {
      // Handle error during API call
      console.error("Error sending time data:", error);
      setError("Failed to send time data. Please try again later.");
    } finally {
      // Reset loading state after operation completes
      setLoading(false);
    }
  };

  const title = translator.translate("V1Manual") + " " + translator.translate("Supervisor") + " " + superName  + "'s " + translator.translate("Employees");
  
  return (
    <PageWrapper title={title} backgroundColor={Colors.White} backArrow={true}>
      <div style={{flexDirection: 'row', display: 'flex', justifyContent: 'center', gap: '20px'}}>
        {/* Select All Checkbox */}
        <div className="select-all-container">
          <input
            type="checkbox"
            checked={selectAll}
            onChange={handleSelectAllChange}
            id="select-all-checkbox"
          />
          <label htmlFor="select-all-checkbox">{translator.translate("SelectAll")}</label>
      </div>
      
        {/* Button to trigger sending data */}
        <button className="select-all-button" onClick={V1Export}>Send Time Data</button>

        {/* Search bar */}
        <SearchBar
          placeholder={"Search Employees"}
          value={searchTerm}
          onChange={setSearchTerm} // Pass the setSearchTerm function to handle changes
          style={{width: '100%', border: "1px solid rgb(111, 111, 111)"}}
        />
      </div>
          {/* Employee Cards */}
          <div>
            {data && filteredEmployees.length > 0 ? (
              filteredEmployees
                .sort((a, b) => {
                  const altA = a.altEmployeeID || '';
                  const altB = b.altEmployeeID || '';
                  return altA.localeCompare(altB);
                })
                .map((employee: Employee) => (
                  <div
                    key={employee.altEmployeeID}
                    className="V1-employee-card"
                    onClick={() => handleEmployeeCheckboxChange(employee.altEmployeeID || '')}
                  >
                    <div className="V1-employee-info-container">
                      {/* Checkbox for each employee */}
                      <div className="V1-employee-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedEmployees.has(employee.altEmployeeID || '')}
                            id={`employee-${employee.altEmployeeID}`}
                          />
                        </div>
                      <div className="V1-employee-info">
                        <h4>{employee.name.last + " - " + employee.name.first + " " + employee.altEmployeeID}</h4>
                      </div>
                    </div>
                  </div>
                ))
            ) : (
              <p className="no-data-message">{translator.translate("NoEmployeesFound")}</p>
            )}
          </div>
    </PageWrapper>
  );
};

export default V1EmployeeListPage;
